import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useCollectionDspTooltip from '../hooks/useCollectionDspTooltip';

/**
 * Renders the hover-tooltip DOM node for collection DSP plots.
 *
 * Same imperative-element + portal approach as CollectionHexagonTooltip:
 * OpenLayers relocates the overlay element into its own container, so React must
 * not own that element directly (it would throw "removeChild" on unmount). We
 * create the element once, hand it to the hook for OL attachment, and portal
 * React content into it.
 *
 * @param {object} props
 * @param {boolean} props.enabled - gate (the plot layer's visibility)
 * @param {import('ol/layer/WebGLVector').default|null} props.layer - the plot layer
 * @param {import('../utils/collectionDspToPlots').DspPlot[]} props.plots
 * @param {Record<string, string>} props.colorByKey - object key (node uuid) → configured color
 */
const CollectionDspTooltip = ({ enabled, layer, plots, colorByKey }) => {
	const overlayElRef = useRef(null);
	if (!overlayElRef.current) {
		overlayElRef.current = document.createElement('div');
		overlayElRef.current.className = 'ol-tooltip';
	}

	const { tooltipContent } = useCollectionDspTooltip(
		enabled,
		layer,
		plots,
		colorByKey,
		overlayElRef
	);

	// Defensive detach on unmount (the hook removes the overlay itself).
	useEffect(() => {
		const el = overlayElRef.current;
		return () => el?.remove();
	}, []);

	return createPortal(tooltipContent, overlayElRef.current);
};

export default CollectionDspTooltip;
